import { CampaignEmission } from './types'
import Colors from '../../../../styles/colors.module.scss'
import { CampaignTarget } from '../../../../types/campaign'
import { CampaignDetailsSliceModel } from '../../models/campaign-details-slice.model'

export default class ChartSupportService {
  static isEmissionsTarget = (target?: CampaignTarget): boolean =>
    target === CampaignTarget.IMPRESSIONS || target === CampaignTarget.FREQUENCY

  static getCellColor = (
    target: CampaignTarget | undefined,
    campaignEmission: CampaignEmission,
    index: number,
    focusBar: number | null
  ): string => {
    if (ChartSupportService.isEmissionsTarget(target) && focusBar === index) {
      return Colors.blue
    }

    if (ChartSupportService.isEmissionsTarget(target)) {
      return Colors.chartBar
    }

    if (campaignEmission.estimated && focusBar === index) {
      return Colors.gray5
    }

    if (campaignEmission.estimated) {
      return Colors.gray4
    }

    if (focusBar === index) {
      return Colors.blue
    }

    return Colors.chartBar
  }

  static getTooltipTopTranslationKey = (
    audienceMode: boolean,
    estimated: boolean | null
  ): string => {
    if (audienceMode && estimated) {
      return 'common.numberOfAudienceEstimated'
    }

    if (audienceMode) {
      return 'common.numberOfAudience'
    }

    return 'common.numberOfEmissions'
  }

  static getTooltipBottomTranslationKey = (
    audienceMode: boolean,
    estimated: boolean | null
  ): string => {
    if (audienceMode && estimated) {
      return 'campaignDetails.chart.sumOfAudienceEstimated'
    }

    if (audienceMode) {
      return 'campaignDetails.chart.sumOfAudience'
    }

    return 'campaignDetails.chart.sumOfEmissions'
  }

  static getYaxisDataKey(
    emissions: CampaignEmission[],
    target?: CampaignTarget,
    dealId?: number | null,
    showTheoretical = false
  ): string {
    const maxEmission = Math.max(...emissions.map(emission => emission.emissions))
    const maxTheoretical = Math.max(
      ...emissions.map(emission => emission.theoreticalEmissions ?? 0)
    )

    /**
     * 1) This is an ordinary campaign (no broadsign data)
     */
    if (!dealId) {
      if (ChartSupportService.isEmissionsTarget(target)) {
        if (showTheoretical && maxTheoretical > maxEmission) {
          return 'theoreticalEmissions'
        }

        return 'emissions'
      }

      return 'audience'
    }

    /**
     * 2) This is a broadsign campaign and broadsign data can contain higher values than emissions or audience
     */
    const maxAudience = Math.max(...emissions.map(emission => emission.audience))
    const maxNoAdCount = Math.max(...emissions.map(emission => emission.noAdCount ?? 0))

    if (ChartSupportService.isEmissionsTarget(target)) {
      const maxLine = showTheoretical ? Math.max(maxEmission, maxTheoretical) : maxEmission

      return maxLine >= maxNoAdCount
        ? showTheoretical && maxTheoretical > maxEmission
          ? 'theoreticalEmissions'
          : 'emissions'
        : 'noAdCount'
    }

    if (target === CampaignTarget.AUDIENCE) {
      return maxAudience >= maxNoAdCount ? 'audience' : 'noAdCount'
    }

    return 'emissions'
  }

  static areFiltersTheSame(
    filters: CampaignDetailsSliceModel['filters'],
    audienceFilters: CampaignDetailsSliceModel['audienceFilters']
  ): boolean {
    const { selected, available } = filters
    const { selected: selectedAudience, available: availableAudience } = audienceFilters

    return (
      selected.agglomerations.length === available.agglomerations.length &&
      selected.cities.length === available.cities.length &&
      selected.buildings.length === available.buildings.length &&
      selected.mediaAsIds.length === available.mediaAsIds.length &&
      selected.pois.length === available.pois.length &&
      selected.creations.length === available.creations.length &&
      selected.broadsignCreationIds.length === available.broadsignCreationIds.length &&
      selectedAudience.sex.length === availableAudience.sex.length &&
      selectedAudience.ageGroups.length === availableAudience.ageGroups.length
    )
  }
}
